import { API } from './index'
import { AnyAction, Dispatch } from 'redux'
import { ChecklistInstance, PartialChecklistInstance } from '../../types/index'
import { logoutUser } from '@mv-submodules/inplant-coreadapter-fe/auth'
import * as Noty from 'noty'
import { CheckPrototype } from '@mv-submodules/inplant-checklist-fe-iblu/ui/components/views/ChecklistEdit/NewCheckModal'
import {
  ChecklistComponentFileData,
  EditComponentMetadataValue,
  PendingUpload,
  PendingUploadErrorKind,
} from '../reducers/checklistEdit'
import {
  CHECK_PHOTOS_TIMEOUT_MS,
  createUploadQueue,
  UPLOAD_TIMEOUT_MS,
  VERIFY_RETRY_DELAY_MS,
} from '../../functions/upload'
import { pickEditState } from '../../hook/useEditChecklist'

// ----------------- Checklist fetching actions -----------------
interface ChecklistEditIsFetchingAction {
  type: 'CHECKLIST_EDIT_IS_FETCHING'
}

interface ChecklistEditFetchSuccessAction {
  type: 'CHECKLIST_EDIT_FETCH_SUCCESS'
  payload: ChecklistInstance | PartialChecklistInstance | undefined
}

interface ChecklistEditFetchErrorAction {
  type: 'CHECKLIST_EDIT_FETCH_ERROR'
  payload: Error
}

const checklistEditIsFetching = (): ChecklistEditIsFetchingAction => {
  return {
    type: 'CHECKLIST_EDIT_IS_FETCHING',
  }
}

const checklistEditFetchSuccess = (
  checklist?: ChecklistInstance | PartialChecklistInstance
): ChecklistEditFetchSuccessAction => {
  return {
    payload: checklist,
    type: 'CHECKLIST_EDIT_FETCH_SUCCESS',
  }
}

const checklistEditFetchError = (error: Error): ChecklistEditFetchErrorAction => {
  return {
    payload: error,
    type: 'CHECKLIST_EDIT_FETCH_ERROR',
  }
}

// ----------------- Checklist submission actions -----------------
interface ChecklistEditIsSubmittingChecklist {
  type: 'CHECKLIST_EDIT_IS_SUBMITTING_CHECKLIST'
}

interface ChecklistEditSubmittedSuccessfully {
  type: 'CHECKLIST_EDIT_SUBMITTED_SUCCESSFULLY'
}

interface ChecklistEditSubmitChecklistError {
  type: 'CHECKLIST_EDIT_SUBMIT_CHECKLIST_ERROR'
  payload: string | undefined
}

const checklistIsSubmitting = (): ChecklistEditIsSubmittingChecklist => {
  return {
    type: 'CHECKLIST_EDIT_IS_SUBMITTING_CHECKLIST',
  }
}

const checklistSubmittedSuccessfully = (): ChecklistEditSubmittedSuccessfully => {
  return {
    type: 'CHECKLIST_EDIT_SUBMITTED_SUCCESSFULLY',
  }
}

const checklistSubmitChecklistError = (error: string | undefined): ChecklistEditSubmitChecklistError => {
  return {
    payload: error,
    type: 'CHECKLIST_EDIT_SUBMIT_CHECKLIST_ERROR',
  }
}

// ----------------- Checklist component actions -----------------
interface ChecklistEditIsModifiedAction {
  type: 'CHECKLIST_EDIT_IS_MODIFIED'
  payload: boolean
}

interface ChecklistEditIsUpdatingComponentAction {
  type: 'CHECKLIST_EDIT_IS_UPDATING_COMPONENT'
  payload: string
}

interface ChecklistEditSetComponentBlurredAction {
  type: 'CHECKLIST_EDIT_SET_COMPONENT_BLURRED'
  payload: string
}

interface ChecklistEditSetComponentErrorAction {
  type: 'CHECKLIST_EDIT_SET_COMPONENT_ERROR'
  payload: {
    componentId: string
    error?: string
  }
}

interface ChecklistEditSetComponentUpdatedAction {
  type: 'CHECKLIST_EDIT_SET_COMPONENT_UPDATED'
  payload: {
    componentId: string
    value: any
  }
}

interface ChecklistEditShowComponentsErrorsAction {
  type: 'CHECKLIST_EDIT_SHOW_COMPONENTS_ERRORS'
}

interface PersistRehydrateAction {
  type: 'persist/REHYDRATE'
}

interface ChecklistEditSetMultiFileComponentFileDeletedAction {
  type: 'CHECKLIST_EDIT_SET_MULTI_FILE_COMPONENT_FILE_DELETED'
  payload: { componentId: string; fileDeleted: string }
}

const isModified = (modified: boolean): ChecklistEditIsModifiedAction => {
  return {
    payload: modified,
    type: 'CHECKLIST_EDIT_IS_MODIFIED',
  }
}

const isUpdatingComponent = (componentId: string): ChecklistEditIsUpdatingComponentAction => {
  return {
    payload: componentId,
    type: 'CHECKLIST_EDIT_IS_UPDATING_COMPONENT',
  }
}

export const setComponentBlurred = (componentId: string): ChecklistEditSetComponentBlurredAction => {
  return {
    payload: componentId,
    type: 'CHECKLIST_EDIT_SET_COMPONENT_BLURRED',
  }
}

const setComponentError = (componentId: string, error?: string): ChecklistEditSetComponentErrorAction => {
  return {
    payload: { componentId, error },
    type: 'CHECKLIST_EDIT_SET_COMPONENT_ERROR',
  }
}

const setComponentUpdated = (
  componentId: string,
  value: EditComponentMetadataValue
): ChecklistEditSetComponentUpdatedAction => {
  return {
    payload: { componentId, value },
    type: 'CHECKLIST_EDIT_SET_COMPONENT_UPDATED',
  }
}

const setMultiFileComponentFileDeleted = (
  componentId: string,
  fileDeleted: string
): ChecklistEditSetMultiFileComponentFileDeletedAction => {
  return {
    payload: { componentId, fileDeleted },
    type: 'CHECKLIST_EDIT_SET_MULTI_FILE_COMPONENT_FILE_DELETED',
  }
}

export const showComponentsErrors = (): ChecklistEditShowComponentsErrorsAction => {
  return {
    type: 'CHECKLIST_EDIT_SHOW_COMPONENTS_ERRORS',
  }
}

// ----------------- Checklist upload actions -----------------
interface ChecklistEditUploadQueuedAction {
  type: 'CHECKLIST_EDIT_UPLOAD_QUEUED'
  payload: PendingUpload
}

interface ChecklistEditUploadStartedAction {
  type: 'CHECKLIST_EDIT_UPLOAD_STARTED'
  payload: { uploadId: string; startedAt: number }
}

interface ChecklistEditUploadVerifyingAction {
  type: 'CHECKLIST_EDIT_UPLOAD_VERIFYING'
  payload: string
}

interface ChecklistEditUploadFailedAction {
  type: 'CHECKLIST_EDIT_UPLOAD_FAILED'
  payload: { uploadId: string; errorKind: PendingUploadErrorKind }
}

interface ChecklistEditUploadSavedAction {
  type: 'CHECKLIST_EDIT_UPLOAD_SAVED'
  payload: { uploadId: string; path: string }
}

interface ChecklistEditUploadDismissedAction {
  type: 'CHECKLIST_EDIT_UPLOAD_DISMISSED'
  payload: string
}

interface ChecklistEditUploadRetriedAction {
  type: 'CHECKLIST_EDIT_UPLOAD_RETRIED'
  payload: string
}

interface ChecklistEditUploadsClearedAction {
  type: 'CHECKLIST_EDIT_UPLOADS_CLEARED'
}

const uploadQueued = (upload: PendingUpload): ChecklistEditUploadQueuedAction => {
  return {
    payload: upload,
    type: 'CHECKLIST_EDIT_UPLOAD_QUEUED',
  }
}

const uploadStarted = (uploadId: string, startedAt: number): ChecklistEditUploadStartedAction => {
  return {
    payload: { uploadId, startedAt },
    type: 'CHECKLIST_EDIT_UPLOAD_STARTED',
  }
}

const uploadVerifying = (uploadId: string): ChecklistEditUploadVerifyingAction => {
  return {
    payload: uploadId,
    type: 'CHECKLIST_EDIT_UPLOAD_VERIFYING',
  }
}

const uploadFailed = (uploadId: string, errorKind: PendingUploadErrorKind): ChecklistEditUploadFailedAction => {
  return {
    payload: { uploadId, errorKind },
    type: 'CHECKLIST_EDIT_UPLOAD_FAILED',
  }
}

const uploadSaved = (uploadId: string, path: string): ChecklistEditUploadSavedAction => {
  return {
    payload: { uploadId, path },
    type: 'CHECKLIST_EDIT_UPLOAD_SAVED',
  }
}

/**
 * Remove a failed upload from the list of the pending uploads.
 */
export const dismissFailedUpload = (uploadId: string): ChecklistEditUploadDismissedAction => {
  return {
    payload: uploadId,
    type: 'CHECKLIST_EDIT_UPLOAD_DISMISSED',
  }
}

const uploadRetried = (uploadId: string): ChecklistEditUploadRetriedAction => {
  return {
    payload: uploadId,
    type: 'CHECKLIST_EDIT_UPLOAD_RETRIED',
  }
}

const uploadsCleared = (): ChecklistEditUploadsClearedAction => {
  return {
    type: 'CHECKLIST_EDIT_UPLOADS_CLEARED',
  }
}

/**
 * Drop all the pending uploads and abort the one in progress: used when the checklist is left.
 *
 * The aborted request could have reached the server anyway, so its photo could be saved.
 */
export const clearPendingUploads = (): ((dispatch: Dispatch<AnyAction>) => void) => {
  return (dispatch: Dispatch<AnyAction>): void => {
    // dropped before aborting, so the upload in progress sees it has been left on purpose
    dispatch(uploadsCleared())
    uploadQueue.abortCurrent()
  }
}

/**
 * Queue again the same file of a failed upload.
 */
export const retryFailedUpload = (
  uploadId: string
): ((dispatch: Dispatch<AnyAction>, getState: () => any) => void) => {
  return (dispatch: Dispatch<AnyAction>, getState: () => any): void => {
    const upload = pickEditState(getState()).pendingUploads[uploadId]

    if (upload?.phase !== 'failed') {
      return
    }

    dispatch(uploadRetried(uploadId))
    _enqueue(upload, dispatch, getState)
  }
}

/**
 * Fetch the checklist data from the server.
 *
 * @param id - The checklist model id.
 */
export const checklistEditFetchChecklist = (
  id: string
): ((dispatch: Dispatch<AnyAction>) => Promise<string | null>) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    dispatch(checklistEditIsFetching())
    try {
      const response = await API().request(`/checklist/data/${id}`)
      const checklist = new ChecklistInstance(response)
      dispatch(checklistEditFetchSuccess(checklist))
      return checklist.id
    } catch (error) {
      _handleEditFetchError(error, dispatch)
      return null
    }
  }
}

/**
 * {@link checklistInstanceId} is the id of the checklist instance.
 *
 * If {@link componentId} is provided, the component will be updated with the new {@link value}.
 *
 * Set {@link conclude} to true to conclude the checklist.
 *
 */
export const checklistUpdateComponent = (
  checklistInstanceId: string,
  componentId: string | undefined,
  value: Array<{ subComponentName: string; value: number | number[] }> | string | number | undefined | null,
  conclude: boolean
): ((dispatch: Dispatch<AnyAction>) => Promise<void>) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    dispatch(isModified(true))

    const componentUpdate = componentId ? { componentId, value } : undefined

    if (componentId) {
      dispatch(isUpdatingComponent(componentId))
    }

    if (conclude) {
      dispatch(checklistIsSubmitting())
    }

    try {
      await API()
        .request(`/checklist/update/${checklistInstanceId}`, {
          headers: {
            'content-type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify({
            updates: componentUpdate ? [componentUpdate] : [],
            conclude,
          }),
        })
        .then(() => {
          if (componentId) {
            dispatch(setComponentUpdated(componentId, value))
          }
          if (conclude) {
            dispatch(checklistSubmittedSuccessfully())
            _notificationConcludedChecklist()
          }
        })
    } catch (error) {
      _handleComponentUpdateError(error, dispatch, componentId)
      if (conclude) {
        dispatch(checklistSubmitChecklistError(_parseFormError(error)))
      }
    }
  }
}

/**
 * Upload a file to the checklist 'file' component.
 *
 * The upload is queued, see {@link _enqueueUploads}.
 *
 * @param componentId - The id of the component to upload the file to.
 * @param checkId - The id of the check the component belongs to.
 * @param file - The file to upload.
 */
export const checklistFileComponentUpload = (
  componentId: string,
  checkId: string,
  file: File
): ((dispatch: Dispatch<AnyAction>, getState: () => any) => void) => {
  return (dispatch: Dispatch<AnyAction>, getState: () => any): void => {
    const component = pickEditState(getState()).componentsMetadata[componentId]

    if (component?.type !== 'file') {
      return
    }

    _enqueueUploads(componentId, checkId, [file], dispatch, getState)
  }
}

/**
 * Get the uploaded file from the checklist 'file' component.
 *
 * @param componentId - The id of the component to get the uploaded file from.
 * @param options - The options for the get.
 */
export const checklistFileComponentGetUploaded = (
  componentId: string,
  options?: { preview: boolean }
): ((dispatch: Dispatch<AnyAction>, getState: () => any) => Promise<void>) => {
  return async (dispatch: Dispatch<AnyAction>, getState: () => any) => {
    try {
      const editState = pickEditState(getState())
      const component = editState.componentsMetadata[componentId]

      if (component.type !== 'file') {
        return
      }

      const value = component.value
      if (!value?.path) {
        return
      }

      dispatch(isUpdatingComponent(componentId))

      const blob = await _getFile(value.path, options)

      dispatch(setComponentUpdated(componentId, { path: value.path, blob }))
    } catch (error) {
      _handleComponentUpdateError(error, dispatch, componentId)
    }
  }
}

/**
 * Upload multiple files to the checklist 'multiFile' component.
 *
 * Each file is queued, see {@link _enqueueUploads}.
 *
 * @param componentId - The id of the component to upload the files to.
 * @param checkId - The id of the check the component belongs to.
 * @param files - The files to upload.
 */
export const checklistMultiFileComponentUploads = (
  componentId: string,
  checkId: string,
  files: File[]
): ((dispatch: Dispatch<AnyAction>, getState: () => any) => void) => {
  return (dispatch: Dispatch<AnyAction>, getState: () => any): void => {
    const component = pickEditState(getState()).componentsMetadata[componentId]

    if (component?.type !== 'multiFile') {
      return
    }

    _enqueueUploads(componentId, checkId, files, dispatch, getState)
  }
}

/**
 * Get the uploaded files from the checklist 'multiFile' component.
 *
 * @param componentId - The id of the component to get the uploaded files from.
 * @param options - The options for the get.
 */
export const checklistMultiFileGetUploaded = (
  componentId: string,
  options?: { preview?: boolean }
): ((dispatch: Dispatch<AnyAction>, getState: () => any) => Promise<void>) => {
  return async (dispatch: Dispatch<AnyAction>, getState: () => any) => {
    try {
      const editState = pickEditState(getState())
      const component = editState.componentsMetadata[componentId]

      if (component.type !== 'multiFile') {
        return
      }

      const value = component.value

      if (!Array.isArray(value)) {
        return
      }

      dispatch(isUpdatingComponent(componentId))
      const filesAdded: ChecklistComponentFileData[] = []
      for (const fileObject of value) {
        try {
          const fileBlob = await _getFile(fileObject.path, options)
          filesAdded.push({ path: fileObject.path, blob: fileBlob })
        } catch (error) {
          _handleError(error, dispatch)
          // the photo is saved anyway: keep it without preview instead of losing the whole component
          filesAdded.push({ path: fileObject.path })
        }
      }

      dispatch(setComponentUpdated(componentId, filesAdded))
    } catch (error) {
      _handleComponentUpdateError(error, dispatch, componentId)
    }
  }
}

/**
 * Delete a file from the checklist 'multiFile' component.
 *
 * @param componentId - The id of the component to delete the file from.
 * @param filePath - The path of the file to delete.
 */
export const checklistMultiFileDeleteFile = (
  componentId: string,
  filePath: string
): ((dispatch: Dispatch<AnyAction>, getState: () => any) => Promise<void>) => {
  return async (dispatch: Dispatch<AnyAction>, getState: () => any) => {
    try {
      const editState = pickEditState(getState())
      const component = editState.componentsMetadata[componentId]

      if (component.type !== 'multiFile') {
        return
      }

      dispatch(isUpdatingComponent(componentId))

      await API().request(`/file/${filePath}`, {
        method: 'DELETE',
      })

      dispatch(setMultiFileComponentFileDeleted(componentId, filePath))
    } catch (error) {
      _handleComponentUpdateError(error, dispatch, componentId)
    }
  }
}

/**
 * Get a file from the checklist.
 *
 * @param filePath - The path of the file to get.
 */
export const checklistGetFileHighResolution = (
  filePath: string
): ((dispatch: Dispatch<AnyAction>) => Promise<Blob | null>) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    try {
      return await _getFile(filePath, { preview: false })
    } catch (error) {
      _handleError(error, dispatch)
      return null
    }
  }
}

/**
 * Go to the next step of the checklist.
 *
 * @param id - The id of the checklist.
 */
export const checklistNextStep = (id: string): ((dispatch: Dispatch<AnyAction>) => Promise<void>) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    dispatch(checklistIsSubmitting())
    try {
      await API().request(`/checklist/next/${id}`, {
        method: 'POST',
      })
      dispatch(checklistSubmittedSuccessfully())
    } catch (error) {
      _handleEditFetchError(error, dispatch)
      dispatch(checklistSubmitChecklistError(_parseFormError(error)))
    }
  }
}

export const addCheck = (
  sectionId: string,
  checkPrototype: CheckPrototype
): ((dispatch: Dispatch<AnyAction>) => Promise<ChecklistInstance>) => {
  return async dispatch => {
    try {
      const response = await API().request(`/checklist/add-check/${sectionId}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(checkPrototype),
      })
      return response
    } catch (error) {
      _handleError(error, dispatch)
    }
  }
}

export const deleteCheck = (checkId: string): ((dispatch: Dispatch<AnyAction>) => Promise<void>) => {
  return async dispatch => {
    try {
      const response = await API().request(`/checklist/delete-check/${checkId}`, {
        method: 'DELETE',
      })
      return response
    } catch (error) {
      _handleError(error, dispatch)
    }
  }
}

export const printChecklist = (checklistId: string): ((dispatch: Dispatch<AnyAction>) => Promise<Blob | null>) => {
  return async dispatch => {
    try {
      const blobData = await API().request(`/checklist/print/${checklistId}`)
      return blobData
    } catch (error) {
      _handleError(error, dispatch)
    }
  }
}

export const exportCsvChecklist = (checklistId: string): ((dispatch: Dispatch<AnyAction>) => Promise<Blob | null>) => {
  return async dispatch => {
    try {
      const data = await API().request(`/checklist/csv/${checklistId}`)
      return new Blob([data], { type: 'application/octet-stream' })
    } catch (error) {
      _handleError(error, dispatch)
      return null
    }
  }
}

export const deleteChecklist = (checklistId: string) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    dispatch(checklistEditIsFetching())
    try {
      await API().request(`/checklist/${checklistId}`, {
        method: 'DELETE',
      })
    } catch (error) {
      _handleError(error, dispatch)
    }
  }
}

/**
 * Uploads are sent one at a time: each photo has the whole bandwidth for its {@link UPLOAD_TIMEOUT_MS} and a photo
 * found on the server while verifying an aborted upload can only be the one just sent.
 */
const uploadQueue = createUploadQueue()

/**
 * Queue a {@link PendingUpload} for each file.
 */
function _enqueueUploads(
  componentId: string,
  checkId: string,
  files: File[],
  dispatch: Dispatch<AnyAction>,
  getState: () => any
) {
  files.forEach(file => {
    const upload: PendingUpload = {
      id: `${componentId}-${Date.now()}-${uploadQueue.nextId()}`,
      componentId,
      checkId,
      file,
      phase: 'queued',
    }

    dispatch(uploadQueued(upload))
    _enqueue(upload, dispatch, getState)
  })
}

function _enqueue(upload: PendingUpload, dispatch: Dispatch<AnyAction>, getState: () => any) {
  uploadQueue.enqueue(signal => _runUpload(upload, signal, dispatch, getState))
}

/**
 * Upload a queued photo:
 * - the photos already on the server for the check are read (baseline);
 * - the photo is sent and, if there is no response within {@link UPLOAD_TIMEOUT_MS}, the request is aborted;
 * - if the request has been aborted or the network failed, the server could have saved the photo anyway, so the
 *   upload is verified against the baseline before declaring it failed.
 *
 * {@link signal} is aborted when the checklist is left, see {@link clearPendingUploads}.
 *
 * Never rejects, so the queue goes on.
 */
async function _runUpload(
  upload: PendingUpload,
  signal: AbortSignal,
  dispatch: Dispatch<AnyAction>,
  getState: () => any
): Promise<void> {
  // dismissed or checklist left in the meantime
  const isDropped = () => !pickEditState(getState()).pendingUploads[upload.id]

  if (isDropped()) {
    return
  }

  try {
    // for the user the baseline is part of the upload: the photo is no longer queued
    dispatch(uploadStarted(upload.id, Date.now()))

    const baseline = await _getCheckPhotos(upload.checkId, signal).catch(() => null)

    if (isDropped()) {
      return
    }

    try {
      const path = await _withTimeout(
        timeoutSignal => _uploadFile(upload.componentId, upload.file, timeoutSignal),
        UPLOAD_TIMEOUT_MS,
        signal
      )
      dispatch(uploadSaved(upload.id, path))
    } catch (error) {
      // aborted on purpose by clearPendingUploads: nothing to verify
      if (isDropped()) {
        return
      }

      const errorKind = _uploadErrorKind(error)

      if (errorKind !== 'timeout' && errorKind !== 'network') {
        _handleError(error, dispatch)
        dispatch(uploadFailed(upload.id, errorKind))
        return
      }

      dispatch(uploadVerifying(upload.id))

      // without a baseline it is not possible to recognize the photo: failed is the safe outcome
      const path = baseline ? await _verifyUpload(upload.checkId, baseline) : null

      dispatch(path ? uploadSaved(upload.id, path) : uploadFailed(upload.id, errorKind))
    }
  } catch (error) {
    dispatch(uploadFailed(upload.id, 'server'))
  }
}

/**
 * Look for a photo of the check that is not in {@link baseline}: immediately and, if not found, once more after
 * {@link VERIFY_RETRY_DELAY_MS}.
 *
 * @returns the path of the new photo, if any.
 */
async function _verifyUpload(checkId: string, baseline: string[]): Promise<string | null> {
  const findNewPhoto = async () => {
    try {
      const photos = await _getCheckPhotos(checkId)
      return photos.find(photo => !baseline.includes(photo)) ?? null
    } catch (error) {
      return null
    }
  }

  const found = await findNewPhoto()
  if (found) {
    return found
  }

  await new Promise(resolve => setTimeout(resolve, VERIFY_RETRY_DELAY_MS))

  return findNewPhoto()
}

function _uploadErrorKind(error: unknown): PendingUploadErrorKind {
  if (error && typeof error === 'object' && (error as { name?: string }).name === 'AbortError') {
    return 'timeout'
  }

  // fetch rejects with a TypeError when the network fails
  if (error instanceof TypeError) {
    return 'network'
  }

  const message = _parseFormError(error) ?? ''

  // 'Invalid Exif: N minutes exceeded by ...'
  if (message.includes('Invalid Exif') && message.includes('exceeded')) {
    return 'exifTooOld'
  }

  // 'Invalid Exif: DateTime is missing' / 'is invalid' or 'exif_read_data(...): File not supported' (e.g. png)
  if (message.toLowerCase().includes('exif')) {
    return 'exifMissing'
  }

  if (message.includes('not an image')) {
    return 'notImage'
  }

  return 'server'
}

/**
 * Call {@link request} with an abort signal fired after {@link timeoutMs} or when {@link signal} is aborted.
 */
async function _withTimeout<T>(
  request: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
  signal?: AbortSignal
): Promise<T> {
  const controller = new AbortController()
  const abort = () => controller.abort()
  const timer = setTimeout(abort, timeoutMs)

  // AbortSignal.any is not available on older Android WebViews
  if (signal?.aborted) {
    abort()
  }
  signal?.addEventListener('abort', abort)

  try {
    return await request(controller.signal)
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', abort)
  }
}

/**
 * The paths of the photos of all the 'file' and 'multiFile' components of the check.
 */
async function _getCheckPhotos(checkId: string, signal?: AbortSignal): Promise<string[]> {
  const response: { photos?: string[] } = await _withTimeout(
    timeoutSignal => API().request(`/checklist/check-data/${checkId}`, { signal: timeoutSignal }, false, true),
    CHECK_PHOTOS_TIMEOUT_MS,
    signal
  )

  return response.photos ?? []
}

async function _uploadFile(componentId: string, file: Blob, signal: AbortSignal): Promise<string> {
  const formData = new FormData()
  formData.append('file', file)

  const response: { file: string } = await API().request(
    `/component/file/${componentId}`,
    {
      method: 'POST',
      body: formData,
      signal,
    },
    false,
    true
  )

  return response.file
}

function _getFile(filePath: string, options?: { preview?: boolean }): Promise<Blob> {
  const qs = options && options.preview ? '?w=640&h=480' : ''
  return API().request(`/file/${filePath}${qs}`)
}

/**
 * Handle errors that are not related to the checklist edit.
 */
function _handleError(error: any, dispatch: Dispatch<AnyAction>) {
  if (error.name === 'FetchError' && error.statusCode === 401) {
    dispatch(logoutUser())
  }
  return null
}

/**
 * Call {@link _handleError} and handle errors that are related to the checklist edit.
 */
function _handleEditFetchError(error: any, dispatch: Dispatch<AnyAction>) {
  _handleError(error, dispatch)
  dispatch(checklistEditFetchError(new Error(error)))
}

/**
 * Call {@link _handleError} and handle errors that are related to the checklist component update.
 */
function _handleComponentUpdateError(error: unknown, dispatch: Dispatch<AnyAction>, componentId: string | undefined) {
  _handleError(error, dispatch)

  if (!componentId) {
    return
  }

  dispatch(setComponentError(componentId, _parseFormError(error, componentId)))
}

/**
 * Parse the error message from the form error.
 *
 * If {@link componentId} is provided, the error message will be parsed from the component error.
 */
function _parseFormError(error: unknown, componentId?: string): string | undefined {
  let errorMessage: string | undefined

  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message: string }).message === 'string'
  ) {
    errorMessage = (error as { message: string }).message
  }

  if (!componentId) {
    return errorMessage
  }

  if (error && typeof error === 'object' && 'errors' in error) {
    const errors = (error as { errors: object }).errors
    if (errors && typeof errors === 'object' && componentId in errors) {
      const componentErrors = (errors as Record<string, unknown>)[componentId]

      if (Array.isArray(componentErrors) && componentErrors.length > 0) {
        errorMessage = componentErrors.toString()
      }

      if (typeof componentErrors === 'string') {
        errorMessage = componentErrors
      }
    }
  }

  return errorMessage
}

function _notificationConcludedChecklist() {
  new Noty({
    text: `
    <div>
      <strong>Operazione completata</strong>
      <br>
      <span>Checklist conclusa con successo</span>
    </div>`,
    type: 'success',
    theme: 'bootstrap-v4',
    layout: 'topRight',
    id: 'fetch-wrapper-noty-container',
    timeout: 5000,
    closeWith: ['click', 'button'],
  }).show()
}

export type ChecklistEditAction =
  | ChecklistEditIsFetchingAction
  | ChecklistEditFetchSuccessAction
  | ChecklistEditFetchErrorAction
  | ChecklistEditIsModifiedAction
  | ChecklistEditIsSubmittingChecklist
  | ChecklistEditSubmittedSuccessfully
  | ChecklistEditSubmitChecklistError
  | ChecklistEditIsUpdatingComponentAction
  | ChecklistEditSetComponentBlurredAction
  | ChecklistEditSetComponentErrorAction
  | ChecklistEditSetComponentUpdatedAction
  | ChecklistEditSetMultiFileComponentFileDeletedAction
  | ChecklistEditShowComponentsErrorsAction
  | ChecklistEditUploadQueuedAction
  | ChecklistEditUploadStartedAction
  | ChecklistEditUploadVerifyingAction
  | ChecklistEditUploadFailedAction
  | ChecklistEditUploadSavedAction
  | ChecklistEditUploadDismissedAction
  | ChecklistEditUploadRetriedAction
  | ChecklistEditUploadsClearedAction
  | PersistRehydrateAction
